import React, { useState, useContext, useEffect, useRef } from 'react';
import {
  Stack,
  Typography,
  Button,
  Avatar,
  Box,
  Modal,
  IconButton,
  Divider,
  TextField,
  InputAdornment,
} from '@mui/material';
import { AlternativeMedicineContext } from 'Context/AlternativeMedicine';
import { useDictionary, useIsDesktop } from 'Hooks';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { LinkArrowLeft2, NextPrevButton } from 'Components';
import { ActionButton } from 'Modules/Reimbursements/Common/ActionButton';
import {
  AccordionArrowPurple,
  AlternativeBG,
  HeartIcon,
  PurpleAlert,
  PurpleClose,
  PurpleXLite,
  ReqPaused,
} from 'Images';
import { useLoader } from 'Hooks/useLoader';
import { ITreatmentType, getTreatmentTypes } from 'API/Reimbursements';
import { TreatmentCard } from './TreatmentCard';
import { BackButton } from '../BackButton';
import { TextWithIcon } from 'Components/TextWithIcon';
import { CustomNewAutoComplete } from '../CustomNewAutoComplete';
import { MAX_TREATMENT_TYPE } from 'Config';
import { handleBackNavigation } from 'Utils/reimbursements';
import { useNavigate } from 'react-router-dom';

export const TreatmentTypeForm = () => {
  const {
    formData,
    setFormData,
    treatmentTypeOptions,
    setShowUploadFileArea,
    setTreatmentTypeOptions,
    setShowTreatmentTypeForm,
    selfTreatment,
    setSelfTreatment,
  } = useContext(AlternativeMedicineContext);
  const contextValues = useContext(AlternativeMedicineContext);
  const { isLoading, loader, setIsLoading } = useLoader(
    'scale(0.25)',
    undefined,
    undefined,
    false
  );
  const dictionary = useDictionary(Dictionary, DICTIONARY.Reimbursements);
  const isDesktop = useIsDesktop();
  const navigate = useNavigate();
  const [selectedTreatment, setSelectedTreatment] = useState<string>(
    formData.treatmentType || ''
  );
  const [selectedTreatmentComment, setSelectedTreatmentComment] = useState('');
  const [recentTreatments, setRecentTreatments] = useState<ITreatmentType[]>(
    []
  );
  const [isAnimatingBtn, setIsAnimatingBtn] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [showErrorNoTreatmentType, setShowErrorNoTreatmentType] =
    useState(false);
  const [showOtherTreatmentForm, setShowOtherTreatmentForm] = useState(false);
  const [showOtherTreatmentFormStep2, setShowOtherTreatmentFormStep2] =
    useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const fetchTreatmentTypes = async () => {
      if (selfTreatment === true) {
        setIsLoading(true);
        try {
          const response = await getTreatmentTypes();
          setTreatmentTypeOptions(response.alternativeTreatmentTypes);
          setRecentTreatments(response.commonAlternativeTreatmentTypes);
        } catch (error) {
          console.error('Error fetching treatment types:', error);
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchTreatmentTypes();
  }, [selfTreatment, setIsLoading, setTreatmentTypeOptions]);

  useEffect(() => {
    if (selectedTreatment && treatmentTypeOptions.length > 0) {
      const treatment = treatmentTypeOptions.find(
        (t) => t.value === selectedTreatment
      );
      setSelectedTreatmentComment(treatment?.comment || '');
    } else {
      setSelectedTreatmentComment('');
    }
  }, [selectedTreatment, treatmentTypeOptions]);

  useEffect(() => {
    if (selectedTreatment) {
      setShowErrorNoTreatmentType(false);
    }
  }, [selectedTreatment]);

  const handleContinue = () => {
    if (selectedTreatment === dictionary.AlternativeMedicine.otherTreatment) {
      handleOtherTreatment();
    } else {
      if (selectedTreatment && formData.invoices.length > 0) {
        setShowTreatmentTypeForm(false);
        setShowUploadFileArea(true);
      } else if (selectedTreatment) {
        setShowTreatmentTypeForm(false);
      } else {
        setShowErrorNoTreatmentType(true);
      }
    }
  };

  const handleButtonClick = () => {
    setIsAnimatingBtn(true);
    setTimeout(() => {
      setIsAnimatingBtn(false);
      setOpenModal(true);
    }, 500);
  };

  const handleOtherTreatment = () => {
    setSelfTreatment(null);
    setShowOtherTreatmentForm(true);
  };

  const handleBackToTreatmentList = () => {
    setSelfTreatment(true);
    setShowOtherTreatmentForm(false);
    setShowOtherTreatmentFormStep2(false);
  };

  const handleClear = () => {
    setFormData({
      ...formData,
      treatmentType: '',
    });
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    setFormData({
      ...formData,
      treatmentType: e.target.value.slice(0, MAX_TREATMENT_TYPE),
    });
  };

  return (
    <Stack spacing={4} maxWidth={'800px'} m={'0 auto'}>
      {selfTreatment === null &&
        (isDesktop ? (
          <>
            <Typography variant='h3' component='h2' align='center'>
              {dictionary.treatmentFor}
            </Typography>
            <Stack
              flexDirection={'row'}
              alignItems={'center'}
              mt={1}
              gap={'5px'}
            >
              <Typography variant='body3' align='center'>
                {dictionary.treatmentTypeSubtitle}
              </Typography>
              <LinkArrowLeft2
                href={dictionary.moreInfoHereLink}
                text={dictionary.moreInfoHere}
                newWindow={true}
              />
            </Stack>

            <Stack spacing={3}>
              <Typography variant='body3' mt={2}>
                {dictionary.receivedTreatmentYourself}
              </Typography>
              <Stack direction='row' spacing={4} justifyContent='start'>
                <ActionButton
                  label={dictionary.yes}
                  onClick={() => setSelfTreatment(true)}
                />
                <ActionButton
                  label={dictionary.noAndMoreOptions}
                  onClick={() => setSelfTreatment(false)}
                />
              </Stack>
            </Stack>
            <Stack mt={'150px !important'}>
              <BackButton />
            </Stack>
            <Avatar
              src={AlternativeBG}
              alt=''
              sx={{
                width: '150px',
                height: '120px',
                position: 'absolute',
                bottom: 0,
                right: '122px',
              }}
            />
          </>
        ) : (
          <>
            <Box textAlign='center'>
              <Typography
                variant='body3'
                component='div'
                sx={{ display: 'inline' }}
              >
                {dictionary.treatmentTypeSubtitle}{' '}
                <Box
                  component='span'
                  sx={{ display: 'inline-block', verticalAlign: 'middle' }}
                >
                  <LinkArrowLeft2
                    href='/'
                    text={dictionary.moreInfoHere}
                    newWindow={true}
                    sx={{
                      '& .MuiTypography-root': {
                        top: '-2px',
                        position: 'relative',
                      },
                    }}
                    variant='body3'
                  />
                </Box>
              </Typography>
            </Box>

            <Stack spacing={3}>
              <Typography variant='body2' mt={2} textAlign={'center'}>
                {dictionary.receivedTreatmentYourself}
              </Typography>
              <Stack direction='column' spacing={4} justifyContent='start'>
                <ActionButton
                  label={dictionary.yes}
                  onClick={() => {
                    setSelfTreatment(true);
                    setOpenModal(false);
                  }}
                  styleSx={!isDesktop && { minWidth: '200px', p: '18px 16px' }}
                />
                <ActionButton
                  label={dictionary.noAndMoreOptions}
                  onClick={() => setSelfTreatment(false)}
                  styleSx={!isDesktop && { minWidth: '200px', p: '18px 16px' }}
                />
              </Stack>
            </Stack>
            <Stack mt={'110px !important'}>
              <BackButton />
            </Stack>
            <Avatar
              src={AlternativeBG}
              alt=''
              sx={{
                width: '95px',
                height: '75px',
                position: 'absolute',
                bottom: '53px',
                right: '30px',
              }}
            />
          </>
        ))}

      {selfTreatment === false && (
        <>
          <Typography variant='h3' component='h2' align='center'>
            {dictionary.reimbursementsForKids}
          </Typography>
          <Stack spacing={2} alignItems='start'>
            <Stack>
              <Stack
                flexDirection={'row'}
                alignItems={'center'}
                mt={1}
                mb={1}
                gap={'5px'}
              >
                <Typography variant='body3' maxWidth={'700px'}>
                  {dictionary.familyTreatmentSubtitle}
                </Typography>
              </Stack>
              <LinkArrowLeft2
                href='/'
                text={dictionary.familyTreatmentsMoreInfo}
                newWindow={true}
              />
              <Box mt={5}>
                <TreatmentCard
                  label={dictionary.familyTreatmentsLabel}
                  icon={HeartIcon}
                  callback={() => {
                    window.open('/universalRequest/1/12/57', '_self');
                  }}
                />
              </Box>
            </Stack>
            <Stack mt={'150px !important'}>
              <NextPrevButton
                callback={() => setSelfTreatment(null)}
                text={dictionary.back}
                style={
                  isDesktop
                    ? undefined
                    : {
                        button: {
                          height: '36px',
                          width: '100%',
                        },
                        text: { fontSize: '16px', fontWeight: '600' },
                      }
                }
              />
            </Stack>
            <Avatar
              src={AlternativeBG}
              alt=''
              sx={
                isDesktop
                  ? {
                      width: '150px',
                      height: '120px',
                      position: 'absolute',
                      bottom: 0,
                      right: '122px',
                    }
                  : {
                      width: '95px',
                      height: '75px',
                      position: 'absolute',
                      bottom: '53px',
                      right: '30px',
                    }
              }
            />
          </Stack>
        </>
      )}

      {!isDesktop && selfTreatment === true && openModal && (
        <Modal
          open={openModal}
          sx={{
            zIndex: '9999',
          }}
        >
          <Stack
            sx={{
              width: '100%',
              height: '100%',
              bgcolor: 'white',
              mt: isDesktop ? '80px' : undefined,
              p: '76px 24px 43px 24px',
              position: 'relative',
              direction: 'ltr',
              justifyContent: 'space-between',
            }}
          >
            <Stack
              sx={{
                height: isDesktop ? 'calc(100vh - 100px)' : undefined,
                overflowY: isDesktop ? 'scroll' : 'auto',
              }}
            >
              <IconButton
                sx={{
                  position: 'absolute',
                  top: isDesktop ? '24px' : '20px',
                  right: '24px',
                }}
                onClick={() => setSelfTreatment(null)}
              >
                <Avatar
                  sx={isDesktop ? undefined : { width: '32px', height: '32px' }}
                  alt={Dictionary[DICTIONARY.General].close}
                  src={PurpleClose}
                />
              </IconButton>

              <Stack
                spacing={2}
                textAlign={'center'}
                sx={isDesktop ? { height: '100%' } : undefined}
              >
                <Typography variant='h3' color='text.secondary'>
                  {dictionary.treatmentType}
                </Typography>
                <Typography variant='body3'>
                  {dictionary.treatmentTypeDoYouReceive}
                </Typography>

                {isLoading ? (
                  <Stack width={'fit-content'}>{loader}</Stack>
                ) : (
                  <Stack>
                    <CustomNewAutoComplete
                      value={selectedTreatment}
                      setValue={(newValue: string | null) => {
                        setSelectedTreatment(newValue || '');
                        setFormData({
                          ...formData,
                          treatmentType: newValue || '',
                        });
                      }}
                      options={treatmentTypeOptions.map((treatment) => ({
                        label: treatment.value,
                        value: treatment.value,
                      }))}
                      recentOptions={recentTreatments
                        .slice(0, 3)
                        .map((treatment) => ({
                          label: treatment.value,
                          value: treatment.value,
                        }))}
                      label={''}
                      placeholder={
                        dictionary.AlternativeMedicine.chooseYourTreatment
                      }
                    />
                  </Stack>
                )}

                {selectedTreatmentComment && (
                  <Stack
                    sx={{
                      '& .MuiTypography-root': {
                        fontSize: '18px',
                        textAlign: 'left',
                        position: 'relative',
                        top: '2px',
                      },
                    }}
                  >
                    <TextWithIcon
                      text={selectedTreatmentComment}
                      icon={PurpleAlert}
                    />
                  </Stack>
                )}
                <Divider
                  sx={{
                    borderColor: '#E7EDFF',
                    m: '160px 0 24px 0 !important',
                  }}
                />
                {showErrorNoTreatmentType && (
                  <Typography
                    variant='body3'
                    color='red'
                    role='alert'
                    textAlign={'center'}
                  >
                    {dictionary.AlternativeMedicine.noTreatmentType}
                  </Typography>
                )}
                <Stack
                  flexDirection={'row'}
                  justifyContent={'space-around'}
                  position={'relative'}
                  top={'-2px'}
                >
                  <NextPrevButton
                    callback={() => setSelfTreatment(null)}
                    text={dictionary.back}
                    style={
                      isDesktop
                        ? undefined
                        : {
                            button: { height: '36px', maxWidth: '121px' },
                            text: { fontSize: '16px', fontWeight: '600' },
                          }
                    }
                  />
                  <NextPrevButton
                    callback={() => handleContinue()}
                    text={dictionary.continue}
                    style={
                      isDesktop
                        ? undefined
                        : {
                            button: { height: '36px', maxWidth: '187px' },
                            text: { fontSize: '16px', fontWeight: '600' },
                          }
                    }
                    isNext
                    isBlue
                  />
                </Stack>
              </Stack>
            </Stack>
          </Stack>
        </Modal>
      )}

      {!isDesktop && selfTreatment === true && !openModal && (
        <Stack>
          <Typography variant='h3' component='h2' align='center'>
            {dictionary.treatmentType}
          </Typography>
          <Typography
            variant='body3'
            color='text.secondary'
            textAlign={'center'}
            mb={2}
          >
            {dictionary.treatmentTypeDoYouReceive}
          </Typography>
          <Button
            endIcon={
              <Avatar
                sx={{ height: '30px', width: '40px' }}
                src={AccordionArrowPurple}
                alt=''
              />
            }
            sx={{
              background: '#F6F8FE !important',
              borderColor: '#B7CCFF !important',
              border: '1px solid',
              borderRadius: '6px',
              width: '100%',
              p: '10px 8px 10px 16px',
              boxShadow: '0px 4px 12px 0px rgba(21, 82, 232, 0.08)',
              justifyContent: 'space-between',
              transition: 'transform 0.5s ease-out, opacity 0.5s ease-out',
              transform: isAnimatingBtn
                ? 'translateY(-200px)'
                : 'translateY(0)',
              opacity: isAnimatingBtn ? 0 : 1,
            }}
            onClick={handleButtonClick}
          >
            <Typography variant='body3' color='text.secondary'>
              {dictionary.chooseFromTheOptionsOrType}
            </Typography>
          </Button>
          <NextPrevButton
            callback={() => setSelfTreatment(null)}
            text={dictionary.back}
            style={
              isDesktop
                ? undefined
                : {
                    button: {
                      mt: '160px',
                      mb: '24px',
                      height: '36px',
                      width: '100%',
                    },
                    text: { fontSize: '16px', fontWeight: '600' },
                  }
            }
          />
        </Stack>
      )}

      {selfTreatment === true && isDesktop && (
        <Stack spacing={2}>
          <Typography variant='h3' component='h2' align='center'>
            {dictionary.treatmentType}
          </Typography>
          <Typography variant='body3'>
            {dictionary.treatmentTypeDoYouReceive}
          </Typography>

          {isLoading ? (
            <Stack width={'fit-content'}>{loader}</Stack>
          ) : (
            <Stack height={'600px'}>
              <CustomNewAutoComplete
                value={selectedTreatment}
                setValue={(newValue: string | null) => {
                  setSelectedTreatment(newValue || '');
                  setFormData({
                    ...formData,
                    treatmentType: newValue || '',
                  });
                }}
                options={treatmentTypeOptions.map((treatment) => ({
                  label: treatment.value,
                  value: treatment.value,
                }))}
                recentOptions={recentTreatments
                  .slice(0, 3)
                  .map((treatment) => ({
                    label: treatment.value,
                    value: treatment.value,
                  }))}
                label={''}
                placeholder={dictionary.AlternativeMedicine.chooseYourTreatment}
              />
              {selectedTreatmentComment && (
                <Stack
                  sx={{
                    '& .MuiTypography-root': {
                      fontSize: '18px',
                      textAlign: 'left',
                      position: 'relative',
                      top: '2px',
                    },
                  }}
                >
                  <TextWithIcon
                    text={selectedTreatmentComment}
                    icon={PurpleAlert}
                  />
                </Stack>
              )}
            </Stack>
          )}

          {showErrorNoTreatmentType && (
            <Typography
              variant='body3'
              color='red'
              role='alert'
              textAlign={'center'}
            >
              {dictionary.AlternativeMedicine.noTreatmentType}
            </Typography>
          )}
          <Stack flexDirection={'row'} justifyContent={'space-around'}>
            <NextPrevButton
              callback={() =>
                handleBackNavigation({
                  isExitBtn: false,
                  navigate,
                  context: contextValues,
                })
              }
              text={dictionary.back}
              style={
                isDesktop
                  ? undefined
                  : {
                      button: { height: '36px' },
                      text: { fontSize: '16px', fontWeight: '600' },
                    }
              }
            />
            <NextPrevButton
              callback={() => handleContinue()}
              text={dictionary.continue}
              style={
                isDesktop
                  ? undefined
                  : {
                      button: { height: '36px' },
                      text: { fontSize: '16px', fontWeight: '600' },
                    }
              }
              isNext
              isBlue
            />
          </Stack>
        </Stack>
      )}

      {showOtherTreatmentForm && (
        <Modal
          open={showOtherTreatmentForm}
          sx={{
            zIndex: '9999',
          }}
        >
          <Stack
            sx={{
              width: '100%',
              height: '100%',
              bgcolor: 'white',
              mt: isDesktop ? '80px' : undefined,
              p: '60px 24px 43px 24px',
              position: 'relative',
              direction: 'ltr',
              justifyContent: 'space-between',
            }}
          >
            <Stack
              sx={{
                height: isDesktop ? 'calc(100vh - 100px)' : undefined,
                overflowY: 'auto',
              }}
            >
              <IconButton
                sx={{
                  position: 'absolute',
                  top: isDesktop ? '24px' : '20px',
                  right: '24px',
                }}
                onClick={() => setShowOtherTreatmentForm(false)}
              >
                <Avatar
                  sx={isDesktop ? undefined : { width: '32px', height: '32px' }}
                  alt={Dictionary[DICTIONARY.General].close}
                  src={PurpleClose}
                />
              </IconButton>

              <Stack
                spacing={2}
                textAlign={'center'}
                sx={isDesktop ? { height: '100%' } : undefined}
              >
                <Typography variant='h3' color='text.secondary'>
                  {`${dictionary.theTreatmentType} ${dictionary.AlternativeMedicine.other}`}
                </Typography>

                {showOtherTreatmentFormStep2 ? (
                  // This is Step 2 of "Other treatment" option
                  <Stack
                    alignSelf={'center'}
                    maxWidth={isDesktop ? '360px' : '100%'}
                  >
                    <Stack>
                      <Typography variant='body3'>
                        {dictionary.treatmentTypeDoYouReceive}
                      </Typography>
                      <TextField
                        autoFocus
                        value={formData.treatmentType || ''}
                        onChange={handleChange}
                        aria-label={dictionary.enterTreatmentTypeHere}
                        required
                        inputRef={inputRef}
                        inputProps={{
                          maxLength: MAX_TREATMENT_TYPE,
                          style: {
                            border: 'none',
                            color: 'secondary.main',
                            width: '100%',
                            backgroundColor: 'primary.shade_50',
                            borderBottom: '2px solid #1552E8',
                            borderRadius: '0px !important',
                            fontSize: '18px',
                            background: '#F6F8FE',
                            padding: '8px',
                            marginLeft: '-40px',
                          },
                        }}
                        sx={{
                          '& fieldset': {
                            border: 'none',
                          },
                          'input::selection': {
                            background: 'transparent',
                          },
                          mt: 3,
                          mb: 10,
                        }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position='end'>
                              <IconButton
                                sx={{ width: '20px', height: '20px' }}
                                onClick={handleClear}
                              >
                                <Avatar
                                  alt={Dictionary[DICTIONARY.General].reset}
                                  src={PurpleXLite}
                                  sx={{ width: '20px', height: '20px' }}
                                />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Stack>

                    <Divider
                      sx={{
                        borderColor: '#E7EDFF',
                        m: '160px 0 48px 0 !important',
                      }}
                    />

                    <Stack
                      flexDirection={'row'}
                      justifyContent={'space-between'}
                      position={'relative'}
                      top={'-2px'}
                    >
                      <NextPrevButton
                        callback={() => {
                          setSelfTreatment(null);
                          setShowOtherTreatmentFormStep2(false);
                        }}
                        text={dictionary.back}
                        style={{
                          button: { height: '36px', maxWidth: '121px' },
                          text: { fontSize: '16px', fontWeight: '600' },
                        }}
                      />

                      <NextPrevButton
                        callback={() => setShowTreatmentTypeForm(false)}
                        text={dictionary.continue}
                        style={{
                          button: { height: '36px', maxWidth: '187px' },
                          text: { fontSize: '16px', fontWeight: '600' },
                        }}
                        isNext
                        isBlue
                        isDisabled={!formData.treatmentType}
                      />
                    </Stack>
                  </Stack>
                ) : (
                  // This is Step 1 of "Other treatment" option
                  <Stack
                    alignSelf={'center'}
                    maxWidth={isDesktop ? '800px' : '100%'}
                  >
                    <Avatar
                      src={ReqPaused}
                      alt=''
                      sx={{
                        width: '88px',
                        height: '60px',
                        m: isDesktop ? '33px auto' : '12px auto',
                      }}
                    />
                    <Stack gap={1}>
                      <Typography variant='h6' fontWeight={600} mb={1}>
                        {dictionary.AlternativeMedicine.oneMoment}
                      </Typography>
                      {isDesktop ? (
                        <>
                          <Typography
                            variant='body3'
                            maxWidth={'550px'}
                            alignSelf={'center'}
                          >
                            {dictionary.AlternativeMedicine.otherTreatment1}{' '}
                            {dictionary.AlternativeMedicine.otherTreatment2}
                          </Typography>
                          <Typography variant='body3'>
                            {dictionary.AlternativeMedicine.otherTreatment3}
                          </Typography>
                        </>
                      ) : (
                        <>
                          <Typography variant='body3'>
                            {dictionary.AlternativeMedicine.otherTreatment1}
                          </Typography>
                          <Typography variant='body3'>
                            {dictionary.AlternativeMedicine.otherTreatment2}
                          </Typography>
                          <Typography variant='body3'>
                            {dictionary.AlternativeMedicine.otherTreatment3}
                          </Typography>
                        </>
                      )}
                    </Stack>
                    <Box m={isDesktop ? '36px auto 24px auto' : '24px'}>
                      <TextWithIcon
                        text={dictionary.AlternativeMedicine.otherTreatmentNote}
                        icon={PurpleAlert}
                      />
                    </Box>

                    <Stack
                      gap={isDesktop ? '24px' : '20px'}
                      mt={isDesktop ? 8 : 2}
                      flexDirection={isDesktop ? 'row-reverse' : 'column'}
                      justifyContent={'center'}
                    >
                      <NextPrevButton
                        callback={() => {
                          setFormData({
                            ...formData,
                            treatmentType: '',
                          });
                          setShowOtherTreatmentFormStep2(true);
                        }}
                        text={dictionary.AlternativeMedicine.goOn}
                        style={{
                          button: {
                            height: isDesktop ? '46px' : '36px',
                            minWidth: isDesktop ? undefined : '100%',
                            width: isDesktop ? '244px' : undefined,
                          },
                          text: { fontSize: '16px', fontWeight: '600' },
                        }}
                        isNext
                        isBlue
                      />
                      <NextPrevButton
                        callback={handleBackToTreatmentList}
                        text={dictionary.AlternativeMedicine.backTreatmentList}
                        style={{
                          button: {
                            height: isDesktop ? '46px' : '36px',
                            minWidth: isDesktop ? undefined : '100%',
                            width: isDesktop ? '244px' : undefined,
                          },
                          text: { fontSize: '16px', fontWeight: '600' },
                        }}
                      />
                    </Stack>
                  </Stack>
                )}
              </Stack>
            </Stack>
          </Stack>
        </Modal>
      )}
    </Stack>
  );
};
